/**
 * 게이트 관리 데모 페이지
 * 간단한 Mock 데이터로 UI만 시연
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheckIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';
import DemoNavigation from '../components/DemoNavigation';

// Mock 게이트 데이터
const MOCK_GATES = [
  {
    id: '1',
    name: '정문 게이트',
    location: '1층 정문',
    status: 'online',
    type: '얼굴인식',
    lastSync: '2025-01-24 14:30:25',
    totalPasses: 245,
    todayPasses: 32,
  },
  {
    id: '2',
    name: '후문 게이트',
    location: '1층 후문',
    status: 'online',
    type: '얼굴인식 + 카드',
    lastSync: '2025-01-24 14:29:50',
    totalPasses: 189,
    todayPasses: 18,
  },
  {
    id: '3',
    name: '비상구 게이트',
    location: '1층 비상구',
    status: 'offline',
    type: '카드',
    lastSync: '2025-01-24 10:15:20',
    totalPasses: 45,
    todayPasses: 0,
  },
  {
    id: '4',
    name: '독서실 입구',
    location: '2층 독서실',
    status: 'online',
    type: '얼굴인식',
    lastSync: '2025-01-24 14:30:15',
    totalPasses: 312,
    todayPasses: 45,
  },
];

const DemoGatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedGate, setSelectedGate] = useState<any>(null);

  const getStatusColor = (status: string) => {
    return status === 'online'
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const getStatusText = (status: string) => {
    return status === 'online' ? '온라인' : '오프라인';
  };

  const getStatusIcon = (status: string) => {
    return status === 'online' ? (
      <CheckCircleIcon className="h-5 w-5 text-green-600" />
    ) : (
      <XCircleIcon className="h-5 w-5 text-red-600" />
    );
  };

  const stats = {
    total: MOCK_GATES.length,
    online: MOCK_GATES.filter((g) => g.status === 'online').length,
    offline: MOCK_GATES.filter((g) => g.status === 'offline').length,
    todayTotal: MOCK_GATES.reduce((sum, g) => sum + g.todayPasses, 0),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 데모 헤더 */}
      <div className="bg-blue-600 text-white py-3 px-4 text-center">
        <p className="text-sm">
          📢 이것은 데모 페이지입니다. 모든 데이터는 가상 데이터이며, 데모 시연을 위해 주기적으로 업데이트되어 조금씩 변동될 수 있습니다.
        </p>
      </div>

      {/* 네비게이션 헤더 */}
      <DemoNavigation currentTitle="게이트 관리 (데모)" currentIcon={ShieldCheckIcon} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 안내 메시지 */}
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 출입 게이트의 상태를 실시간으로 모니터링하고 관리할 수 있습니다!
          </p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">전체 게이트</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}개</p>
              </div>
              <ShieldCheckIcon className="h-12 w-12 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">온라인</p>
                <p className="text-2xl font-bold text-green-600">{stats.online}개</p>
              </div>
              <CheckCircleIcon className="h-12 w-12 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">오프라인</p>
                <p className="text-2xl font-bold text-red-600">{stats.offline}개</p>
              </div>
              <XCircleIcon className="h-12 w-12 text-red-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">오늘 출입</p>
                <p className="text-2xl font-bold text-purple-600">{stats.todayTotal}회</p>
              </div>
              <WifiIcon className="h-12 w-12 text-purple-500" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 게이트 목록 */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">게이트 목록</h2>
              <button
                onClick={() => alert('데모 모드에서는 게이트 추가가 제한됩니다.')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              >
                <PlusIcon className="h-4 w-4" />
                게이트 추가
              </button>
            </div>

            {MOCK_GATES.map((gate) => (
              <div
                key={gate.id}
                onClick={() => setSelectedGate(gate)}
                className={`bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-all ${
                  selectedGate?.id === gate.id ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{gate.name}</h3>
                    <p className="text-sm text-gray-500">{gate.location}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(gate.status)}
                    <span
                      className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(gate.status)}`}
                    >
                      {getStatusText(gate.status)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">인증 방식</p>
                    <p className="text-sm font-medium text-gray-900">{gate.type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">마지막 동기화</p>
                    <p className="text-sm font-medium text-gray-900">{gate.lastSync}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">오늘 출입</p>
                    <p className="text-sm font-medium text-blue-600">{gate.todayPasses}회</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">총 출입</p>
                    <p className="text-sm font-medium text-gray-900">{gate.totalPasses}회</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 게이트 상세 정보 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">게이트 상세</h2>
            {selectedGate ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">게이트명</p>
                  <p className="text-lg font-bold text-gray-900">{selectedGate.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">위치</p>
                  <p className="text-md font-medium text-gray-900">{selectedGate.location}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">상태</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusIcon(selectedGate.status)}
                    <span
                      className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedGate.status)}`}
                    >
                      {getStatusText(selectedGate.status)}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">인증 방식</p>
                  <p className="text-md font-medium text-gray-900">{selectedGate.type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">마지막 동기화</p>
                  <p className="text-md font-medium text-gray-900">{selectedGate.lastSync}</p>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">출입 통계</p>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">오늘</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {selectedGate.todayPasses}회
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">전체</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {selectedGate.totalPasses}회
                      </span>
                    </div>
                  </div>
                </div>
                <div className="pt-4 space-y-2">
                  <button
                    onClick={() => alert('데모 모드에서는 재시작이 제한됩니다.')}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    게이트 재시작
                  </button>
                  <button
                    onClick={() => alert('데모 모드에서는 설정 변경이 제한됩니다.')}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    설정 변경
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <ShieldCheckIcon className="mx-auto h-16 w-16 text-gray-300" />
                <p className="mt-4 text-sm text-gray-500">
                  게이트를 선택하면 상세 정보를 확인할 수 있습니다.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoGatesPage;
